import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

// "Hide amounts": balances and spending totals are replaced by a mask so the app can be used with
// someone nearby. The choice is remembered on the device. It is a screen-privacy aid, not a security
// boundary — the figures are still in the data the app loaded.

const KEY = "barnyard:amounts-hidden";
const MASK = "••••••";
const PEEK_MS = 5000;

interface PrivacyState {
  hidden: boolean;
  toggle: () => void;
}

const PrivacyContext = createContext<PrivacyState>({ hidden: false, toggle: () => {} });

function stored() {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function PrivacyProvider({ children }: { children: ReactNode }) {
  const [hidden, setHidden] = useState(stored);
  const toggle = useCallback(() => {
    setHidden((value) => {
      try {
        localStorage.setItem(KEY, value ? "0" : "1");
      } catch {
        // Storage can be unavailable (private mode); the choice then lasts until reload.
      }
      return !value;
    });
  }, []);
  const value = useMemo(() => ({ hidden, toggle }), [hidden, toggle]);
  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
}

export const usePrivacy = () => useContext(PrivacyContext);

/** For text that cannot hold markup — aria-labels, titles, chart tooltips and axes. */
export function useMask() {
  const { hidden } = usePrivacy();
  return useCallback((text: string) => (hidden ? MASK : text), [hidden]);
}

/** Wraps one figure. While amounts are hidden it shows a mask; tapping the mask shows just that
 * figure for a few seconds. When shown, it renders its children unchanged. */
export function Private({ children }: { children: ReactNode }) {
  const { hidden } = usePrivacy();
  const [peeking, setPeeking] = useState(false);
  useEffect(() => {
    if (!peeking) return;
    const timer = setTimeout(() => setPeeking(false), PEEK_MS);
    return () => clearTimeout(timer);
  }, [peeking]);
  useEffect(() => {
    if (!hidden) setPeeking(false);
  }, [hidden]);
  if (!hidden || peeking) return <>{children}</>;
  return (
    <span
      className="private-mask"
      role="button"
      tabIndex={0}
      aria-label="Hidden amount. Tap to show."
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setPeeking(true);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        setPeeking(true);
      }}
    >
      {MASK}
    </span>
  );
}
